/**
 * CockpitLoader — full-screen brand loading overlay used during route
 * transitions (e.g. login → dashboard, navigation between authenticated
 * pages). Picks one of 5 animation variants at random on mount and runs it
 * once over ~1500ms with eased progress, then fades out and calls onComplete.
 *
 * Ported from landing/components/cockpit/CockpitLoader.tsx so the SPA shares
 * the same brand transition language as credia.pro. Pure React + RAF — no
 * Next.js or framework dependency.
 *
 * Usage: don't render this directly. Trigger it through the
 * useTransitionLoader store (see ../../stores/transitionLoaderStore.ts) which
 * mounts/unmounts <CockpitLoaderOverlay /> at the App root.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import TerminalBoot from './loaders/TerminalBoot';
import CandlestickBuild from './loaders/CandlestickBuild';
import GridCellFill from './loaders/GridCellFill';
import RadialGauge from './loaders/RadialGauge';
import DataWaterfall from './loaders/DataWaterfall';

const LOADERS = [TerminalBoot, CandlestickBuild, GridCellFill, RadialGauge, DataWaterfall];
const DEFAULT_DURATION = 1500; // ms for 0→100%

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface Props {
  /** Fired after the animation finishes and fades. Optional when `holdAtComplete`. */
  onComplete?: () => void;
  /** Override the random pick (mostly for tests / showcase). */
  variant?: number;
  /** ms for 0→100%. Default 1500. Use a shorter value to match a fast wait. */
  durationMs?: number;
  /**
   * When true, the loader ramps to 100% and STAYS there (no fade, no onComplete).
   * Use for a splash whose end is driven externally — e.g. the checkout tab that
   * gets navigated to Stripe once the session is ready, at an unknown moment.
   */
  holdAtComplete?: boolean;
}

export default function CockpitLoader({
  onComplete,
  variant,
  durationMs = DEFAULT_DURATION,
  holdAtComplete = false,
}: Props) {
  // -1 = not yet selected. Randomized in the mount effect so the picked
  // variant is stable across the loader's lifetime.
  const [activeIdx, setActiveIdx] = useState(variant ?? -1);
  useEffect(() => {
    if (variant === undefined) {
      setActiveIdx(Math.floor(Math.random() * LOADERS.length));
    }
  }, [variant]);

  const [progress, setProgress] = useState(0);
  const [fading, setFading] = useState(false);
  const startRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const completedRef = useRef(false);

  // prefers-reduced-motion → skip animation, dispatch onComplete immediately
  // so the consumer can route-change without staring at a static screen.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      setReducedMotion(true);
      onComplete?.();
    }
  }, [onComplete]);

  const startAnimation = useCallback(() => {
    completedRef.current = false;
    startRef.current = performance.now();
    setProgress(0);
    setFading(false);

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const t = Math.min(elapsed / durationMs, 1);
      setProgress(easeInOutCubic(t) * 100);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (!completedRef.current) {
        completedRef.current = true;
        setProgress(100);
        // Splash mode: reach 100% and stay — the caller navigates away when ready.
        if (holdAtComplete) {return;}
        // Brief hold at 100, then fade-out, then onComplete (which unmounts).
        setTimeout(() => {
          setFading(true);
          setTimeout(() => {
            onComplete?.();
          }, 300);
        }, 200);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [onComplete, durationMs, holdAtComplete]);

  useEffect(() => {
    if (!reducedMotion) {startAnimation();}
    return () => {
      if (rafRef.current) {cancelAnimationFrame(rafRef.current);}
    };
  }, [startAnimation, reducedMotion]);

  if (reducedMotion) {return null;}

  const ActiveLoader = activeIdx >= 0 ? LOADERS[activeIdx] : null;

  return (
    <div
      className="cockpit-loader-overlay"
      style={{ opacity: fading ? 0 : 1 }}
      aria-hidden="true"
    >
      {ActiveLoader && <ActiveLoader progress={progress} />}
    </div>
  );
}
